import { useState } from 'react';
import { Download, Mail, MessageCircle, Smartphone } from 'lucide-react';
import { addReport } from '../store';
import { Report } from '../types';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useApp } from '../providers/AppProvider';
import { generateId } from '../utils/idGenerator';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { Progress } from '../components/ui/progress';
import SurfaceCard from '../components/SurfaceCard';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
} from '../constants/widgetTheme';

export default function Reports() {

  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedSprint, setSelectedSprint] = useState<string>('all');
  const [reportType, setReportType] = useState<'summary' | 'sprint' | 'team' | 'time'>('summary');
  const { state, loading, reload } = useApp();
  const { projects, tasks, sprints, users } = useOrganizationData(state) as {
    projects: any[];
    tasks: any[];
    sprints: any[];
    users: any[];
  };

  if (loading || !state) {
    return <div className="p-8 text-center">Loading reports...</div>;
  }

  const filteredTasks = selectedProject === 'all'
    ? tasks
    : tasks.filter(t => t.projectId === selectedProject);

  const filteredSprints = selectedProject === 'all'
    ? sprints
    : sprints.filter(s => s.projectId === selectedProject);

  const scopedReports = (state.reports || []).filter(
    (report) => report.organizationId === state.currentOrganization?.id
  );

  // Task completion over time
  const tasksByMonth = filteredTasks.reduce((acc, task) => {
    if (task.status === 'Completed') {
      const month = new Date(task.updatedAt).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      acc[month] = (acc[month] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const completionData = Object.entries(tasksByMonth).map(([month, count]) => ({
    month,
    completed: count,
  }));

  // Tasks by status
  const tasksByStatus = [
    { name: 'Active', value: filteredTasks.filter(t => t.status === 'Active').length, color: '#3b82f6' },
    { name: 'In Progress', value: filteredTasks.filter(t => t.status === 'In Progress').length, color: '#8b5cf6' },
    { name: 'Completed', value: filteredTasks.filter(t => t.status === 'Completed').length, color: '#10b981' },
    { name: 'Deferred', value: filteredTasks.filter(t => t.status === 'Deferred').length, color: '#f59e0b' },
    { name: 'Blocked', value: filteredTasks.filter(t => t.status === 'Blocked').length, color: '#ef4444' },
  ];

  // Team performance
  const teamPerformance = users.map(user => {
    const userTasks = filteredTasks.filter(t => t.assigneeIds.includes(user.id));
    const completed = userTasks.filter(t => t.status === 'Completed').length;
    const totalHours = userTasks.reduce((sum, task) => 
      sum + task.timeLogs
        .filter((log: any) => log.userId === user.id)
        .reduce((s: number, l: any) => s + l.hours, 0), 0
    );
    return {
      name: user.name.split(' ')[0],
      completed,
      hours: totalHours,
    };
  }).filter(u => u.completed > 0 || u.hours > 0);

  // Sprint summary
  const sprintData = filteredSprints.map(sprint => {
    const sprintTasks = tasks?.filter(t => sprint.taskIds?.includes(t.id)) || [];
    const completed = sprintTasks.filter(t => t.status === 'Completed').length;
    const total = sprintTasks.length;
    return {
      name: sprint.name,
      completed,
      remaining: total - completed,
      completion: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  });

  const handleCreateReport = async (cadence: string, channels: ('email' | 'whatsapp' | 'sms')[]) => {
    const report: Report = {
      id: generateId('report', selectedProject !== 'all' ? selectedProject : undefined),
      organizationId: state.currentOrganization?.id || state.currentUser?.organizationId || '',
      name: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`,
      type: 'custom',
      cadence,
      projectId: selectedProject !== 'all' ? selectedProject : undefined,
      sprintId: selectedSprint !== 'all' ? selectedSprint : undefined,
      filters: { reportType, selectedProject, selectedSprint },
      createdAt: new Date().toISOString(),
      notificationChannels: channels,
    };
    await addReport(report);
    const email = state.currentUser?.email;
    if (email) {
      await reload(email);
    }
    alert(`Report scheduled! It will be sent via: ${channels.join(', ')}`);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Reports & Analytics</h1>
        <p className={WIDGET_MUTED_TEXT_CLASS}>Track progress and generate insights across projects</p>
      </div>

      {/* Filters */}
      <div className={`${WIDGET_PANEL_CLASS} mb-6 rounded-lg p-4`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            title="Select report type"
            value={reportType}
            onChange={(e) => setReportType(e.target.value as any)}
            className={APP_NATIVE_FIELD_CLASS}
          >
            <option value="summary">Summary Report</option>
            <option value="sprint">Sprint Report</option>
            <option value="team">Team Performance</option>
            <option value="time">Time Tracking</option>
          </select>

          <select
            title="Select project"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className={APP_NATIVE_FIELD_CLASS}
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>

          {filteredSprints.length > 0 && (
            <select
              title="Select sprint"
              value={selectedSprint}
              onChange={(e) => setSelectedSprint(e.target.value)}
              className={APP_NATIVE_FIELD_CLASS}
            >
              <option value="all">All Sprints</option>
              {filteredSprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Export & Schedule Actions */}
      <div className={`${WIDGET_ALERT_INFO_CLASS} mb-6 rounded-lg p-6`}>
        <h3 className="text-lg mb-4">Export & Schedule Reports</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => alert('Downloading report as PDF... (Feature placeholder)')}
            className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-foreground hover:bg-accent"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <button
            onClick={() => handleCreateReport('0 9 * * 1', ['email'])}
            className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-foreground hover:bg-accent"
          >
            <Mail className="w-4 h-4" />
            Schedule Weekly Email
          </button>
          <button
            onClick={() => handleCreateReport('0 18 * * *', ['whatsapp'])}
            className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-foreground hover:bg-accent"
          >
            <MessageCircle className="w-4 h-4" />
            Schedule Daily WhatsApp
          </button>
          <button
            onClick={() => handleCreateReport('0 18 * * *', ['sms'])}
            className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-foreground hover:bg-accent"
          >
            <Smartphone className="w-4 h-4" />
            Schedule Daily SMS
          </button>
        </div>
          <p className={`mt-3 text-sm ${WIDGET_ALERT_INFO_TEXT_CLASS}`}>
          Note: Notification integration requires configuration with external services (Gmail, WhatsApp, SMS providers)
        </p>
      </div>

      {/* Summary Report */}
      {reportType === 'summary' && (
        <div className="space-y-6">
          {/* Task Completion Over Time */}
          <SurfaceCard className="p-6">
            <h2 className="text-xl mb-4">Task Completion Trend</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={completionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="completed" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </SurfaceCard>

          {/* Tasks by Status */}
          <SurfaceCard className="p-6">
            <h2 className="text-xl mb-4">Tasks by Status</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={tasksByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tasksByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </SurfaceCard>
        </div>
      )}

      {/* Sprint Report */}
      {reportType === 'sprint' && (
        <SurfaceCard className="p-6">
          <h2 className="text-xl mb-4">Sprint Progress</h2>
          {sprintData.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No sprint data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={sprintData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" fill="#10b981" name="Completed" />
                <Bar dataKey="remaining" fill="#6b7280" name="Remaining" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SurfaceCard>
      )}

      {/* Team Performance Report */}
      {reportType === 'team' && (
        <SurfaceCard className="p-6">
          <h2 className="text-xl mb-4">Team Performance</h2>
          {teamPerformance.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No team performance data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={teamPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="completed" fill="#3b82f6" name="Tasks Completed" />
                <Bar yAxisId="right" dataKey="hours" fill="#8b5cf6" name="Hours Logged" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SurfaceCard>
      )}

      {/* Time Tracking Report */}
      {reportType === 'time' && (
        <SurfaceCard className="p-6">
          <h2 className="text-xl mb-4">Time Tracking Summary</h2>
          <div className="space-y-4">
            {users.map(user => {
              const userTasks = filteredTasks.filter(t => t.assigneeIds.includes(user.id));
              const totalEstimated = userTasks.reduce((sum, task) => sum + task.estimatedDays * 8, 0);
              const totalLogged = userTasks.reduce((sum, task) => 
                sum + task.timeLogs
                  .filter((log: any) => log.userId === user.id)
                  .reduce((s: number, l: any) => s + l.hours, 0), 0
              );

              if (totalEstimated === 0 && totalLogged === 0) return null;

              return (
                <div key={user.id} className="rounded-lg border border-border bg-muted/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4>{user.name}</h4>
                    <span className="text-sm text-muted-foreground">
                      {totalLogged}h / {totalEstimated}h
                    </span>
                  </div>
                  <Progress
                    value={Math.min((totalLogged / totalEstimated) * 100, 100)}
                    className={
                      totalLogged > totalEstimated
                        ? 'h-2 [&::-webkit-progress-value]:bg-destructive/10 [&::-moz-progress-bar]:bg-destructive/10 dark:[&::-webkit-progress-value]:bg-destructive/10 dark:[&::-moz-progress-bar]:bg-destructive/10'
                        : 'h-2 [&::-webkit-progress-value]:bg-chart-2/10 [&::-moz-progress-bar]:bg-chart-2/10 dark:[&::-webkit-progress-value]:bg-chart-2/10 dark:[&::-moz-progress-bar]:bg-chart-2/10'
                    }
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {userTasks.length} task{userTasks.length !== 1 ? 's' : ''} assigned
                  </p>
                </div>
              );
            })}
          </div>
        </SurfaceCard>
      )}

      {/* Scheduled Reports */}
      {scopedReports.length > 0 && (
        <SurfaceCard className="mt-6 p-6">
          <h2 className="text-xl mb-4">Scheduled Reports</h2>
          <div className="space-y-3">
            {scopedReports.map((report) => (
              <div key={report.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-4">
                <div>
                  <h4 className="mb-1">{report.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    Cadence: {report.cadence || 'One-time'} | Channels: {report.notificationChannels.join(', ')}
                  </p>
                </div>
                <span className="text-sm text-muted-foreground">
                  Created {new Date(report.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
}
